import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark bg-background text-foreground grid lg:grid-cols-2">
      <div className="hidden lg:block relative blueprint-grid border-r border-border">
        <div className="absolute inset-0 grid place-items-center p-12">
          <div className="max-w-md">
            <div className="h-12 w-12 rounded-xl bg-primary grid place-items-center mb-6"><HardHat className="h-6 w-6 text-primary-foreground" /></div>
            <h2 className="text-3xl font-extrabold tracking-tight">Start estimating in minutes.</h2>
            <p className="mt-4 text-muted-foreground">Create your workspace and generate your first AI-priced BOQ today.</p>
          </div>
        </div>
      </div>
      <div className="grid place-items-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h1 className="text-2xl font-bold tracking-tight mb-1">Create account</h1>
          <p className="text-sm text-muted-foreground mb-6">No credit card required.</p>

          <button type="button" onClick={googleLogin} data-testid="google-register-btn"
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-card py-2.5 font-semibold hover:bg-secondary transition-colors mb-4">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <label className="block text-sm font-semibold mb-1.5">Full name</label>
          <input data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="Jane Estimator" />
          <label className="block text-sm font-semibold mb-1.5">Email</label>
          <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-4 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="you@company.com" />
          <label className="block text-sm font-semibold mb-1.5">Password</label>
          <input data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-input bg-card px-3 py-2.5 mb-6 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition" placeholder="At least 6 characters" />

          <button data-testid="register-submit-btn" disabled={loading}
            className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 active:bg-orange-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
          </button>
          <p className="text-sm text-muted-foreground mt-5 text-center">
            Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="to-login-link">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
